// The shared redaction and sensitivity helpers for telemetry (CHT-770, Phase 13.4). ONE set of rules, used before EVERY export: the Sentry
// events of the API, the admin and the mobile app, the structured API logs, the operational diagnostics and the agent trace attributes.
// Pure: no I/O, no clock, no vendor type. A vendor hook (`beforeSend`) calls these functions; the functions know no vendor.
//
// Two walls, as for analytics:
//  1. KEYS. A key that names a sensitive class (a token, an auth header, a signature, a message text, a memory value, an internal note, a
//     payment provider secret, a provider payload, a contact detail) has its whole value replaced, whatever the value looks like. A key of the
//     correlation allowlist (`lafayette.*`, `trigger.run_id`) is kept when its value is an id-shaped token.
//  2. VALUES. A string is scanned for secret-like shapes (a JWT, a bearer header, a vendor key prefix, a connection string, a long opaque
//     token, an email, a phone number, a URL with a query or a user) and the match is replaced, whatever the key says.
//
// Telemetry is NEVER proof of a payment, a request or a confirmation. This file protects what leaves the process; it makes no claim about state.
export const REDACTED = "[redacted]";
/** The classes of sensitive data. A closed list: a log or a test names the class, never the value. */
export const sensitiveClasses = Object.freeze([
    "TOKEN",
    "AUTH_HEADER",
    "SIGNATURE",
    "MESSAGE_TEXT",
    "MEMORY_VALUE",
    "INTERNAL_NOTE",
    "PAYMENT_SECRET",
    "PROVIDER_PAYLOAD",
    "CONTACT",
    "FREE_TEXT",
]);
const normalize = (key) => key.toLowerCase().replace(/[^a-z0-9]/g, "");
// Keys are compared normalized (lower case, no separators), so `Authorization`, `x-razorpay-signature`, `razorpay_key_secret` and
// `razorpayKeySecret` all match. Exact names first, then substrings that are unambiguous.
const EXACT = new Map([
    ["authorization", "AUTH_HEADER"],
    ["proxyauthorization", "AUTH_HEADER"],
    ["cookie", "AUTH_HEADER"],
    ["setcookie", "AUTH_HEADER"],
    ["xapikey", "AUTH_HEADER"],
    ["apikey", "TOKEN"],
    ["token", "TOKEN"],
    ["secret", "TOKEN"],
    ["password", "TOKEN"],
    ["passwd", "TOKEN"],
    ["jwt", "TOKEN"],
    ["bearer", "TOKEN"],
    ["session", "TOKEN"],
    ["credentials", "TOKEN"],
    ["dsn", "TOKEN"],
    ["signature", "SIGNATURE"],
    ["xrazorpaysignature", "SIGNATURE"],
    ["xsignature", "SIGNATURE"],
    ["xstreamsignature", "SIGNATURE"],
    ["svixsignature", "SIGNATURE"],
    ["message", "MESSAGE_TEXT"],
    ["messages", "MESSAGE_TEXT"],
    ["text", "MESSAGE_TEXT"],
    ["body", "MESSAGE_TEXT"],
    ["content", "MESSAGE_TEXT"],
    ["prompt", "MESSAGE_TEXT"],
    ["transcript", "MESSAGE_TEXT"],
    ["reply", "MESSAGE_TEXT"],
    ["response", "MESSAGE_TEXT"],
    ["note", "INTERNAL_NOTE"],
    ["notes", "INTERNAL_NOTE"],
    ["comment", "INTERNAL_NOTE"],
    ["payload", "PROVIDER_PAYLOAD"],
    ["rawbody", "PROVIDER_PAYLOAD"],
    ["requestbody", "PROVIDER_PAYLOAD"],
    ["responsebody", "PROVIDER_PAYLOAD"],
    ["requestbodyvalues", "PROVIDER_PAYLOAD"],
    ["email", "CONTACT"],
    ["phone", "CONTACT"],
    ["phonee164", "CONTACT"],
    ["address", "CONTACT"],
    ["fullname", "CONTACT"],
    ["firstname", "CONTACT"],
    ["lastname", "CONTACT"],
    ["preferredname", "CONTACT"],
    ["username", "CONTACT"],
    ["ip", "CONTACT"],
    ["ipaddress", "CONTACT"],
]);
const CONTAINS = [
    ["authorization", "AUTH_HEADER"],
    ["apikey", "TOKEN"],
    ["accesstoken", "TOKEN"],
    ["refreshtoken", "TOKEN"],
    ["idtoken", "TOKEN"],
    ["secretkey", "TOKEN"],
    ["clientsecret", "TOKEN"],
    ["privatekey", "TOKEN"],
    ["authtoken", "TOKEN"],
    ["webhooksecret", "PAYMENT_SECRET"],
    ["keysecret", "PAYMENT_SECRET"],
    ["razorpaysecret", "PAYMENT_SECRET"],
    ["signingsecret", "PAYMENT_SECRET"],
    ["signature", "SIGNATURE"],
    ["cardnumber", "PAYMENT_SECRET"],
    ["cvv", "PAYMENT_SECRET"],
    ["vpa", "PAYMENT_SECRET"],
    ["upiid", "PAYMENT_SECRET"],
    ["accountnumber", "PAYMENT_SECRET"],
    ["ifsc", "PAYMENT_SECRET"],
    ["messagetext", "MESSAGE_TEXT"],
    ["memoryvalue", "MEMORY_VALUE"],
    ["factvalue", "MEMORY_VALUE"],
    ["memorytext", "MEMORY_VALUE"],
    ["internalnote", "INTERNAL_NOTE"],
    ["requesttext", "FREE_TEXT"],
    ["specialrequests", "FREE_TEXT"],
    ["freetext", "FREE_TEXT"],
    ["providerpayload", "PROVIDER_PAYLOAD"],
    ["rawpayload", "PROVIDER_PAYLOAD"],
    ["webhookbody", "PROVIDER_PAYLOAD"],
    ["emailaddress", "CONTACT"],
    ["phonenumber", "CONTACT"],
];
/**
 * The sensitive class that a key names, or `null`. The match is on the normalized key, so spelling and case do not matter. A key that is a
 * correlation name (`lafayette.*`, `trigger.run_id`) is never sensitive.
 */
export function sensitiveClassOfKey(key) {
    if (isCorrelationKey(key))
        return null;
    const flat = normalize(key);
    if (flat === "")
        return null;
    const exact = EXACT.get(flat);
    if (exact !== undefined)
        return exact;
    for (const [part, kind] of CONTAINS)
        if (flat.includes(part))
            return kind;
    // `value` and `values` under a memory or a fact: the key says what the value is.
    if (flat.includes("memory") || flat.includes("fact")) {
        if (flat.endsWith("value") || flat.endsWith("values") || flat.endsWith("text")) {
            return "MEMORY_VALUE";
        }
    }
    if (flat.endsWith("secret") || flat.endsWith("token") || flat.endsWith("password")) {
        return "TOKEN";
    }
    // A key that ends in a content word names content: `customer_note`, `status_message`, `reply_text`, `request_body`.
    if (flat.endsWith("note") || flat.endsWith("notes") || flat.endsWith("comment"))
        return "INTERNAL_NOTE";
    if (flat.endsWith("message") || flat.endsWith("text") || flat.endsWith("body"))
        return "MESSAGE_TEXT";
    return null;
}
// --- correlation names (the only keys that are kept on sight) ------------------------------------------------------------------------
/** The names that link a signal to a domain record. Ids, versions, codes and numbers only. See `telemetry-correlation.ts`. */
export const CORRELATION_KEY_PREFIX = "lafayette.";
// `requestId` and `correlationId` are the plain names of the same two ids in the request log line of the API (`apps/api/src/logging.ts`). They are
// kept like their `lafayette.*` twins. Without this, a request id such as `61912249-4861-474a-...` was read as a phone number in one field and
// kept in the other (CHT-2052).
export const CORRELATION_EXTRA_KEYS = new Set([
    "trigger.run_id",
    "requestId",
    "correlationId",
]);
export const isCorrelationKey = (key) => key.startsWith(CORRELATION_KEY_PREFIX) || CORRELATION_EXTRA_KEYS.has(key);
/** An id-shaped token: letters, digits and `._:-`, 1 to 128 characters. A correlation value that is not one is redacted. */
const ID_TOKEN = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/;
// --- value patterns -------------------------------------------------------------------------------------------------------------------
const JWT = /\beyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{4,}\.[A-Za-z0-9_-]{0,}/g;
const BEARER = /\b(?:Bearer|Basic|Token)\s+[A-Za-z0-9._~+/=-]{8,}/gi;
// Vendor key prefixes: Stripe-style `sk_`, Razorpay `rzp_`, webhook `whsec_`, PostHog `phc_`/`phx_`, Sentry `sntrys_`/`sntryu_`, Resend `re_`,
// the `os_` prefix of an old OneSignal key, Supabase `sb_secret_`/`sb_publishable_`, GitHub `ghp_`, Google `AIza`.
const VENDOR_KEY = /\b(?:sk|pk|rk|rzp|whsec|phc|phx|sntrys|sntryu|sb_secret|sb_publishable|ghp|gho|re|os)_[A-Za-z0-9_]{8,}|\bAIza[0-9A-Za-z_-]{20,}/g;
// An Expo push token (`ExponentPushToken[...]` or `ExpoPushToken[...]`): the secret of a device (CHT-764). The pattern is loose on purpose:
// whatever sits between the brackets is removed, so a token of a new length or alphabet is still caught.
const EXPO_PUSH_TOKEN = /\bExpo(?:nent)?PushToken\[[^\]\s]{1,256}\]?/gi;
const CONNECTION_STRING = /\b[a-z][a-z0-9+.-]*:\/\/[^\s/@:]+:[^\s/@]+@[^\s]+/gi;
const URL_WITH_QUERY = /\b(https?:\/\/[^\s?#]+)[?#][^\s]*/gi;
const URL_USERINFO = /\b(https?:\/\/)[^\s/@]+@/gi;
const EMAIL = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;
const PHONE = /(?<![\w.])\+?\d[\d\s().-]{8,}\d(?![\w.])/g;
// A long opaque run: a key, a signature, a hash of a secret. 40 or more characters from a base64, base64url or hex alphabet with no
// separator. A uuid (36 characters with dashes) and a git sha (40 hex) are kept: a sha is an id, and the pattern excludes pure hex of 40.
const LONG_OPAQUE = /(?<![A-Za-z0-9+/_=-])(?<!@\d+\.\d+\.)(?=[A-Za-z0-9+/_=-]*[A-Za-z])(?=[A-Za-z0-9+/_=-]*\d)[A-Za-z0-9+/_=-]{40,}(?![A-Za-z0-9+/_=-])/g;
const GIT_SHA = /^[0-9a-f]{40}$/;
// --- build identity (CHT-1975) ------------------------------------------------------------------------------------------------------------
//
// A build id such as `local-20261018123456789` holds a long run of digits, so the phone pattern would redact it. Weakening that pattern would
// let real phone numbers through. So the build identity has its own rule, BY KEY and with a strict value shape. The rule applies to the TAGS
// `build_id`, `dist` and `release` and to the top-level `release` and `dist` of an event, and to nothing else: a nested value in `extra`, in a
// context or in a breadcrumb keeps the old behavior. The value is checked as it is, never after a change. A run of 6 or more digits is allowed
// at one place only: the time stamp at the end of a build id, and only in the three shapes that the build scripts write.
//
// What the code writes (the source of the shapes below):
//  - `scripts/deploy-staging.mjs`: `local-<YYYYMMDDHHMMSS + 3 digits>`, for example `local-20261018123456789`.
//  - `scripts/pre-ci.mjs`: `local-pre-ci-<Date.now()>`, 13 digits, for example `local-pre-ci-1789000000000`.
//  - A name with a short sha or a number: `incident-kit-proof-0001`, `acceptance-device-a87bebe`, `build-77`. A default: `local`.
//  - The release: `lafayette-<surface>@<version>+<sha>`. The version is `local`, `development`, `staging`, `pre-ci` or a semantic version. The
//    sha is a git sha (7 to 40 hex) or `local`.
/** The keys of the tags that carry the build identity. `dist` is the tag that Sentry derives from the `dist` field of an event. */
export const BUILD_IDENTITY_KEYS = new Set(["build_id", "dist", "release"]);
const BUILD_ID_SHAPE = /^[A-Za-z0-9][A-Za-z0-9.-]{0,63}$/;
// The time stamp at the end of a build id: `YYYYMMDDHHMMSS` with an optional 3 digits of milliseconds, or the epoch in milliseconds (13 digits).
const COMPACT_TIME = "20\\d\\d(?:0[1-9]|1[0-2])(?:0[1-9]|[12]\\d|3[01])(?:[01]\\d|2[0-3])[0-5]\\d[0-5]\\d(?:\\d{3})?";
const EPOCH_MS = "1[5-9]\\d{11}";
const BUILD_ID_TIMESTAMP = new RegExp(`^(.+)-(?:${COMPACT_TIME}|${EPOCH_MS})$`);
const RELEASE_SHAPE = /^lafayette-(?:api|admin|mobile|worker)@([A-Za-z0-9.-]{1,32})\+([A-Za-z0-9]{1,40})$/;
/** `true` when `text` holds no secret shape (a JWT, a vendor key, an e-mail address, a phone number, a long opaque run) and no run of 6 or more digits. */
const plainName = (text) => !/\d{6,}/.test(text) && redactString(text) === text;
/**
 * A build id. It has 1 to 64 letters, digits, `.` and `-`, and no `_`, `@`, space or bracket. It holds no run of 6 or more digits, except the
 * time stamp at the end (`<name>-<YYYYMMDDHHMMSS[mmm]>` or `<name>-<epoch ms>`). The rest of the name, and the whole value, still have to pass
 * the secret patterns. A value of digits only is a build id only up to 6 digits (`77`, `4242`).
 */
export function isBuildId(value) {
    if (typeof value !== "string" || !BUILD_ID_SHAPE.test(value))
        return false;
    const withTime = BUILD_ID_TIMESTAMP.exec(value);
    if (withTime !== null) {
        const name = withTime[1] ?? "";
        // The time stamp is not a phone number here, so it is replaced by one digit for the secret patterns. The name must be plain.
        return plainName(name) && redactString(`${name}-0`) === `${name}-0` && !/^\d+$/.test(name);
    }
    return plainName(value);
}
/** A release name as the apps write it: `lafayette-<surface>@<version>+<sha>`, the sha in hex (7 to 40 characters) or `local`. */
export function isReleaseName(value) {
    if (typeof value !== "string")
        return false;
    const parts = RELEASE_SHAPE.exec(value);
    if (parts === null)
        return false;
    const [, version = "", sha = ""] = parts;
    return plainName(version) && (sha === "local" || /^[0-9a-f]{7,40}$/.test(sha));
}
/** The value of a build identity key: kept when it has the shape of that key, else `[redacted]`. */
function buildIdentityValue(key, value) {
    if (key === "release")
        return isReleaseName(value) ? value : REDACTED;
    return isBuildId(value) ? value : REDACTED;
}
/** Replaces the secret-like shapes in a text. Idempotent: running it twice gives the same text. */
export function redactString(text) {
    let out = text;
    out = out.replace(EXPO_PUSH_TOKEN, REDACTED);
    out = out.replace(CONNECTION_STRING, REDACTED);
    out = out.replace(JWT, REDACTED);
    out = out.replace(BEARER, REDACTED);
    out = out.replace(VENDOR_KEY, REDACTED);
    out = out.replace(URL_USERINFO, "$1");
    out = out.replace(URL_WITH_QUERY, "$1");
    out = out.replace(EMAIL, REDACTED);
    out = out.replace(PHONE, REDACTED);
    out = out.replace(LONG_OPAQUE, (match) => (GIT_SHA.test(match) ? match : REDACTED));
    return out;
}
/** `true` when `redactString` would change the text. Used by tests and by the production guard of log writers. */
export const looksSensitive = (text) => redactString(text) !== text;
const DEFAULTS = { maxDepth: 8, maxStringLength: 512, maxArrayLength: 50, maxKeys: 100 };
/**
 * A copy of `value` that is safe to export. It never mutates the input, survives a cycle, and never throws.
 * - A key of a sensitive class: the whole value becomes `[redacted]`, nested or not.
 * - A correlation key: kept when the value is an id-shaped string or a number; else `[redacted]`.
 * - A string: `redactString`, then cut. A number, a boolean and `null` pass. A function, a symbol and a bigint become `[redacted]`.
 * - An `Error`: only its `name` and the redacted `message` (never the `stack`, `cause` or extra fields, which can echo a request).
 */
export function redactValue(value, options = {}) {
    const limits = { ...DEFAULTS, ...options };
    const seen = new WeakSet();
    const walk = (input, depth, key) => {
        if (key !== null) {
            if (isCorrelationKey(key)) {
                if (typeof input === "number" && Number.isFinite(input))
                    return input;
                return typeof input === "string" && ID_TOKEN.test(input) ? input : REDACTED;
            }
            if (sensitiveClassOfKey(key) !== null)
                return REDACTED;
        }
        if (input === null || typeof input === "boolean")
            return input;
        if (typeof input === "number")
            return Number.isFinite(input) ? input : REDACTED;
        if (typeof input === "string") {
            const clean = redactString(input);
            return clean.length > limits.maxStringLength
                ? `${clean.slice(0, limits.maxStringLength)}…`
                : clean;
        }
        if (input === undefined)
            return undefined;
        if (typeof input !== "object")
            return REDACTED;
        if (depth >= limits.maxDepth || seen.has(input))
            return REDACTED;
        seen.add(input);
        if (input instanceof Error) {
            return { name: input.name, message: walk(input.message, depth + 1, null) };
        }
        if (input instanceof Date)
            return Number.isNaN(input.getTime()) ? REDACTED : input.toISOString();
        if (Array.isArray(input)) {
            return input.slice(0, limits.maxArrayLength).map((item) => walk(item, depth + 1, null));
        }
        const out = {};
        let count = 0;
        for (const [name, inner] of Object.entries(input)) {
            if (count >= limits.maxKeys)
                break;
            count += 1;
            // The key itself may be a secret or an email (a caller made it up): it is redacted as a string and never kept raw.
            const safeName = redactString(name);
            out[safeName === name ? name : REDACTED] = walk(inner, depth + 1, name);
        }
        return out;
    };
    try {
        return walk(value, 0, null);
    }
    catch {
        return REDACTED;
    }
}
/** The classes found in a value, for a test or a diagnostic: names only, never a value. Each class once, in the registry order. */
export function sensitiveClassesIn(value) {
    const found = new Set();
    const seen = new WeakSet();
    const walk = (input, depth) => {
        if (depth > 8)
            return;
        if (typeof input === "string") {
            if (looksSensitive(input))
                found.add("TOKEN");
            return;
        }
        if (typeof input !== "object" || input === null || seen.has(input))
            return;
        seen.add(input);
        for (const [key, inner] of Object.entries(input)) {
            const kind = sensitiveClassOfKey(key);
            if (kind !== null && inner !== null && inner !== undefined && inner !== "")
                found.add(kind);
            walk(inner, depth + 1);
        }
    };
    walk(value, 0);
    return sensitiveClasses.filter((kind) => found.has(kind));
}
const isRecord = (value) => typeof value === "object" && value !== null && !Array.isArray(value);
/**
 * The only request headers that may leave. Everything else (authorization, cookie, signatures, vendor keys, ip headers) is dropped.
 * `user-agent` is NOT on the list (CHT-1975): Sentry reads it on its server and builds the `browser`, `os` and `device` contexts and tags
 * from it, so a client that sends it brings those fields back after the shared scrub removed them.
 */
export const EXPORTABLE_REQUEST_HEADERS = new Set([
    "content-type",
    "content-length",
    "accept",
    "x-request-id",
    "x-correlation-id",
]);
/**
 * The contexts that may leave (CHT-1975). Default-deny: the SDKs add `device`, `os`, `app`, `culture`, `browser`, `cloud_resource` and the like,
 * which name a model, a kernel, a locale with a region, a time zone or a memory size. Only the trace link and the runtime stay.
 */
export const EXPORTABLE_CONTEXTS = new Set(["trace", "runtime"]);
/**
 * The tags that may leave (CHT-1975). Default-deny: a tag that is not here, and is not a correlation name (`lafayette.*`, `trigger.run_id`), is
 * removed. The names are compared in lower case. They are the build identity, the tags that the SDKs and the smoke path set (`level`, `handled`,
 * `mechanism`, `runtime`, `event.origin`, `event.environment`, `smoke_test`) and the tags that the API code sets on a capture
 * (`request_id`, `correlation_id`, `actor_kind`, `route_class`, `agent_run`, and `run_id`, `model_key`, `error_code` of an agent run).
 * The review script reads this same set, so the rule exists once.
 */
export const EXPORTABLE_TAGS = new Set([
    "app_surface",
    "build_id",
    "dist",
    "git_sha",
    "environment",
    "release",
    "level",
    "handled",
    "mechanism",
    "smoke_test",
    "runtime",
    "runtime.name",
    "event.origin",
    "event.environment",
    "request_id",
    "correlation_id",
    "actor_kind",
    "route_class",
    "agent_run",
    "run_id",
    "model_key",
    "error_code",
]);
/**
 * The tag names that name a device, an operating system, a browser, a place, a host or a page: `os`, `os.name`, `os.build`, `device`,
 * `device.family`, `browser`, `browser.name`, `geo`, `locale`, `timezone`, `client_os`, `url` and `server_name`, in any case. They are removed
 * because they are not in `EXPORTABLE_TAGS`; the review script uses this pattern to name the class of a flag.
 */
export const DEVICE_TAG_NAME = /^(?:device|os|browser|geo|locale|timezone|client_os|url|server_name)(?:\.|$)/i;
function scrubHeaders(headers) {
    if (!isRecord(headers))
        return {};
    const out = {};
    for (const [name, value] of Object.entries(headers)) {
        if (!EXPORTABLE_REQUEST_HEADERS.has(name.toLowerCase()))
            continue;
        out[name] = redactValue(value);
    }
    return out;
}
/** A request URL without its query string, fragment and user info, with secret-like shapes replaced. */
export function scrubUrl(url) {
    const cut = url.replace(/[?#].*$/, "");
    return redactString(cut);
}
function scrubBag(value) {
    return value === undefined ? undefined : redactValue(value);
}
/** The contexts of the allow-list, redacted. Any other context (`device`, `os`, `app`, `culture`, `browser`) is removed. */
function scrubContexts(contexts) {
    if (!isRecord(contexts))
        return scrubBag(contexts);
    const kept = {};
    for (const [name, value] of Object.entries(contexts)) {
        if (EXPORTABLE_CONTEXTS.has(name))
            kept[name] = value;
    }
    return scrubBag(kept);
}
/**
 * The tags of the allow-list (`EXPORTABLE_TAGS`, in any case) and the correlation names, redacted. The tags `build_id`, `dist` and `release`
 * keep a value only with the strict shape of a build id or a release name (CHT-1975). A tag list that is not an object goes through the deep
 * redaction as it is.
 */
function scrubTags(tags) {
    if (!isRecord(tags))
        return scrubBag(tags);
    const kept = {};
    const identity = {};
    for (const [name, value] of Object.entries(tags)) {
        const lower = name.toLowerCase();
        if (BUILD_IDENTITY_KEYS.has(lower))
            identity[name] = buildIdentityValue(lower, value);
        else if (EXPORTABLE_TAGS.has(lower) || isCorrelationKey(name))
            kept[name] = value;
    }
    return { ...scrubBag(kept), ...identity };
}
// --- default-deny for the TEXT of an exception and of a span ------------------------------------------------------------------------------
//
// Pattern matching removes only the secret SHAPES it knows. A customer message, a memory value or an internal note is prose: it has no shape.
// So the text of an exception, of a captured message and of a span is not exported unless it is an approved CODE. Everything else leaves as
// `[redacted]`. The code throws closed codes (`new Error("TOOL_FAILED")`); the class name (`type`) stays because it is a name in the source.
/**
 * An approved error text: a closed code (`TOOL_FAILED`, `ECONNREFUSED`), a SQLSTATE (`57P03`) or a class name that ends in `Error` or
 * `Exception`. A text with a space, a quote, an `@` or lower-case prose does not match.
 */
const APPROVED_ERROR_TEXT = /^(?:[A-Z][A-Z0-9_]{1,63}|[0-9A-Z]{5}|[A-Z][A-Za-z0-9]{0,62}(?:Error|Exception))$/;
const CLASS_NAME = /^[A-Za-z_$][A-Za-z0-9_$.]{0,127}$/;
/** The text of an exception, a message or a log entry as it may leave: the approved code, or `[redacted]`. */
export function approvedErrorText(text) {
    return APPROVED_ERROR_TEXT.test(text) ? text : REDACTED;
}
/** A short name that a span or a tag may keep: letters, digits and `._:/-`. A sentence, a quote or an `@` does not match. */
const SAFE_SPAN_TOKEN_SHAPE = /^[A-Za-z0-9][A-Za-z0-9._:/-]{0,127}$/;
/** The shape, and no secret shape inside (a long opaque token or a signature is a token by shape and a secret by value). */
const SAFE_SPAN_TOKEN = {
    test: (text) => SAFE_SPAN_TOKEN_SHAPE.test(text) && redactString(text) === text,
};
const HTTP_SPAN = /^([A-Z]{3,7}) (\S{1,400})$/;
/** The description of a span: `METHOD url` (the query is cut), a plain name, or `[redacted]` (a query text, a sentence). */
function approvedSpanDescription(description) {
    const http = HTTP_SPAN.exec(description);
    if (http?.[2] !== undefined) {
        const url = scrubUrl(http[2]);
        return SAFE_SPAN_TOKEN.test(url) || /^https?:\/\/[A-Za-z0-9._:/-]+$/.test(url)
            ? `${http[1]} ${url}`
            : REDACTED;
    }
    return SAFE_SPAN_TOKEN.test(description) ? description : REDACTED;
}
/** The data of a span: numbers, booleans and correlation ids stay; a string stays only as a short token; a URL loses its query. */
function scrubSpanData(data) {
    if (!isRecord(data))
        return scrubBag(data);
    const out = {};
    for (const [key, value] of Object.entries(data)) {
        if (!isCorrelationKey(key) && sensitiveClassOfKey(key) !== null) {
            out[key] = REDACTED;
        }
        else if (typeof value === "string" && !isCorrelationKey(key)) {
            const text = /url|target|route|path/i.test(key) ? scrubUrl(value) : value;
            out[key] =
                SAFE_SPAN_TOKEN.test(text) || /^https?:\/\/[A-Za-z0-9._:/-]+$/.test(text) ? text : REDACTED;
        }
        else {
            out[key] = redactValue({ [key]: value })[key];
        }
    }
    return out;
}
/**
 * Makes a Sentry-shaped event (an error event, a transaction or a breadcrumb bag) safe to export. It is typed loosely on purpose: the node,
 * browser and react-native SDKs have different event types, and this function must serve all three. It returns a NEW object.
 *
 * What changes:
 * - `request`: headers cut to `EXPORTABLE_REQUEST_HEADERS`, `cookies`, `data`, `env` and `query_string` removed, `url` without query.
 * - `user`: only `id` is kept when it is an id-shaped token. Lafayette sets no user, so an id is the installation id that the mobile SDK adds
 *   (policy section 4.4). No email, no ip, no name, no geo.
 * - `contexts`: only `EXPORTABLE_CONTEXTS` (`trace`, `runtime`) stay. `tags`: only `EXPORTABLE_TAGS` and the correlation names stay (CHT-1975).
 * - `release` and `dist`: kept only with the strict shape of a release name and of a build id (`isReleaseName`, `isBuildId`), else `[redacted]`.
 * - `message`, `exception.values[].value`, `logentry.message`: DEFAULT-DENY (CHT-770 review). Only an approved code (`approvedErrorText`) leaves;
 *   prose becomes `[redacted]`. The stack frames keep file, function and line; their `vars` go.
 * - `extra`, `contexts`, `tags`, `breadcrumbs[].data` and `message`: deep redaction (correlation keys kept).
 * - `spans[].description` and `spans[].data`: DEFAULT-DENY for strings (a short token, `METHOD url` without its query, or `[redacted]`).
 * - `server_name`, `modules`, `sdkProcessingMetadata`: removed (host names and dependency lists are not needed to reproduce a failure).
 * Release, environment, `app_surface`, `build_id`, `git_sha` and the correlation tags pass through.
 */
export function scrubTelemetryEvent(event) {
    const input = event;
    const out = { ...input };
    delete out.server_name;
    delete out.modules;
    delete out.sdkProcessingMetadata;
    // The release and the dist are not walked by the bags below. A value that is not a release name or a build id (an e-mail address, a token)
    // must not leave through them.
    for (const field of ["release", "dist"]) {
        if (input[field] !== undefined)
            out[field] = buildIdentityValue(field, input[field]);
    }
    if (isRecord(input.request)) {
        const request = input.request;
        const kept = { ...request };
        kept.headers = scrubHeaders(request.headers);
        delete kept.cookies;
        delete kept.data;
        delete kept.query_string;
        // The server environment of a request (`REMOTE_ADDR` and the like) names the caller's address.
        delete kept.env;
        if (typeof request.url === "string")
            kept.url = scrubUrl(request.url);
        out.request = kept;
    }
    if (isRecord(input.user)) {
        const id = input.user.id;
        out.user = typeof id === "string" && ID_TOKEN.test(id) ? { id } : {};
    }
    if (typeof input.message === "string")
        out.message = approvedErrorText(input.message);
    if (isRecord(input.logentry)) {
        const entry = input.logentry;
        out.logentry = {
            ...(typeof entry.message === "string" ? { message: approvedErrorText(entry.message) } : {}),
            // The parameters of a formatted message are free values: they never leave.
        };
    }
    if (isRecord(input.exception) && Array.isArray(input.exception.values)) {
        out.exception = {
            ...input.exception,
            values: input.exception.values.map((entry) => {
                if (!isRecord(entry))
                    return REDACTED;
                const value = { ...entry };
                if (typeof entry.value === "string")
                    value.value = approvedErrorText(entry.value);
                if (typeof entry.type === "string" && !CLASS_NAME.test(entry.type))
                    value.type = REDACTED;
                // The mechanism and the module name are not needed to find the failure.
                delete value.module;
                if (isRecord(entry.stacktrace) && Array.isArray(entry.stacktrace.frames)) {
                    value.stacktrace = {
                        ...entry.stacktrace,
                        frames: entry.stacktrace.frames.map((frame) => {
                            if (!isRecord(frame))
                                return REDACTED;
                            const { vars: _vars, ...rest } = frame;
                            return rest;
                        }),
                    };
                }
                return value;
            }),
        };
    }
    if (input.extra !== undefined)
        out.extra = scrubBag(input.extra);
    if (input.contexts !== undefined)
        out.contexts = scrubContexts(input.contexts);
    if (input.tags !== undefined)
        out.tags = scrubTags(input.tags);
    if (Array.isArray(input.breadcrumbs)) {
        out.breadcrumbs = input.breadcrumbs.map((crumb) => isRecord(crumb) ? scrubBreadcrumbData(crumb) : REDACTED);
    }
    if (Array.isArray(input.spans)) {
        out.spans = input.spans.map((span) => {
            if (!isRecord(span))
                return REDACTED;
            const copy = { ...span };
            if (copy.data !== undefined)
                copy.data = scrubSpanData(copy.data);
            if (typeof copy.description === "string")
                copy.description = approvedSpanDescription(copy.description);
            return copy;
        });
    }
    return out;
}
/** A breadcrumb with its message and data redacted. The category, level, type and time are kept. */
export function scrubBreadcrumbData(breadcrumb) {
    if (!isRecord(breadcrumb))
        return breadcrumb;
    const out = { ...breadcrumb };
    // A console line and a UI event hold free text or the label of what a person touched: prose cannot be detected, so neither the message
    // nor the data leaves. The category, the level and the time stay, so the order of events is still visible.
    const category = typeof breadcrumb.category === "string" ? breadcrumb.category : "";
    if (category === "console" || category.startsWith("ui.")) {
        delete out.message;
        delete out.data;
        return out;
    }
    if (typeof breadcrumb.message === "string")
        out.message = redactString(breadcrumb.message);
    if (breadcrumb.data !== undefined) {
        const data = redactValue(breadcrumb.data);
        // A fetch or xhr breadcrumb has a `url`: it loses its query string too.
        if (isRecord(data) && typeof breadcrumb.data.url === "string") {
            data.url = scrubUrl(breadcrumb.data.url);
        }
        out.data = data;
    }
    return out;
}
